import { Container, Graphics, Sprite, TextStyle, Texture, Text } from "pixi.js";
import { IArtworkSprite, Cluster, NodeData } from "../interfaces";
import { generateConvexHull } from "./math";
import { isDebugMode } from "../config";
import { SimulationNodeDatum } from "d3-force";
import { ArtworkSprite } from "../sprites/ArtworkSprite";

export const drawControlPoint = (
  graphics: any,
  point: any,
  color = 0xff0000,
  radius = 5,
) => {
  graphics.beginFill(color);
  graphics.drawCircle(point.x, point.y, radius);
  graphics.endFill();
};

export const drawTaperedCurveWithOpacity = (
  container: any,
  start: { x: number; y: number },
  control: { x: number; y: number },
  end: { x: number; y: number },
  startWidth: number,
  endWidth: number,
  color: number,
  startOpacity: number = 1,
): void => {
  const pathGraphics = new Graphics();
  const STEPS = 20;

  // Pre-calculate coefficients
  const points = new Array(STEPS + 1);
  const widthDiff = endWidth - startWidth;
  const endOpacity = 0; // Fade to fully transparent
  const opacityDiff = endOpacity - startOpacity;

  for (let i = 0; i <= STEPS; i++) {
    const t = i / STEPS;
    const mt = 1 - t;
    const mt2 = mt * mt;
    const t2 = t * t;
    const tmt = 2 * mt * t;

    points[i] = {
      x: mt2 * start.x + tmt * control.x + t2 * end.x,
      y: mt2 * start.y + tmt * control.y + t2 * end.y,
      width: startWidth + t * widthDiff,
      alpha: startOpacity + t * opacityDiff,
    };
  }

  // Batch render segments
  pathGraphics.beginPath();

  for (let i = 0; i < STEPS; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];

    pathGraphics.setStrokeStyle({
      width: (p1.width + p2.width) * 0.5,
      color,
      alpha: (p1.alpha + p2.alpha) * 0.5,
    });

    pathGraphics.moveTo(p1.x, p1.y);
    pathGraphics.lineTo(p2.x, p2.y);
    pathGraphics.stroke();
  }

  container.addChild(pathGraphics);
};

// Add this helper function to draw cluster outlines
export const drawClusterOutline = (cluster: Cluster, container: Container) => {
  if (!cluster.children) return;
  // Calculate the convex hull points
  const hull = generateConvexHull(cluster.boundary!);

  // Draw the outline
  const graphics = new Graphics();
  graphics.clear();
  graphics.setStrokeStyle({ width: 2 }); // Adjust line style as needed

  // Draw the path
  graphics.beginPath();
  graphics.moveTo(hull[0].x, hull[0].y);

  // Draw lines to each subsequent point
  for (let i = 1; i < hull.length; i++) {
    graphics.lineTo(hull[i].x, hull[i].y);
  }

  // Close the path
  graphics.lineTo(hull[0].x, hull[0].y);
  graphics.closePath();

  graphics.stroke();

  container.addChild(graphics);
};

export const calculateRadius = (sprite: Sprite): number => {
  const bounds = sprite.getLocalBounds();
  return Math.max(bounds.width, bounds.height) / 2;
};

export const createArtworkSprite = (
  uuid: string,
  texture: Texture,
  isCenterNode: boolean,
  defaultScaleFactor = 50,
  centerScaleFactor = 500,
): IArtworkSprite => {

  const sprite = new ArtworkSprite(texture) as IArtworkSprite;
  sprite.anchor.set(0.5);

  const maxDimension = Math.max(texture.width, texture.height);
  const scaleFactor = (isCenterNode ? centerScaleFactor : defaultScaleFactor) / maxDimension;

  sprite.scaleFactor = scaleFactor;

  sprite.width = texture.width * scaleFactor;
  sprite.height = texture.height * scaleFactor;

  sprite.isCentered = isCenterNode;
  sprite.radius = calculateRadius(sprite);
  sprite.interactive = true;
  sprite.cursor = "pointer";
  sprite.eventMode = "static";

  if (isDebugMode) {
    const radiusCircle = new Graphics();
    radiusCircle.setStrokeStyle({ width: 2, color: 0x00ff00, alpha: 1 });
    radiusCircle.circle(0, 0, sprite.radius);
    radiusCircle.fill();
    radiusCircle.alpha = 0.5;

    sprite.addChild(radiusCircle);
  }

  sprite.label = uuid;
  return sprite;
};

export const addArtworkSpriteEvents = (
  sprite: IArtworkSprite,
  node: any,
  events?: {
    onSpriteOver?: (sprite: Sprite) => void;
    onSpriteOut?: (sprite: Sprite) => void;
    onPointerTap?: (sprite: Sprite, node: any) => void;
  },
): IArtworkSprite => {
  // Event Handlers
  sprite.on("mouseover", () => {
    if (events?.onSpriteOver) {
      events.onSpriteOver(sprite);
    }
  });

  sprite.on("mouseout", () => {
    if (events?.onSpriteOut) {
      events.onSpriteOut(sprite);
    }
  });

  sprite.on("pointertap", () => {
    if (events?.onPointerTap) {
      events.onPointerTap(sprite, node);
    }
  });
  return sprite;
};


export const drawDebugBounds = (layer:Container ,container: Container) => {
  if (!container) return;

  // Get the container bounds
  const bounds = container.getBounds();

  // Create a new Graphics object for debugging
  const debugGraphics = new Graphics();

  // Draw the bounds rectangle
  debugGraphics.setStrokeStyle({
    color: 0xff0000,
    width: 2,
    alpha: 1,
  }); // Red color, 2px line width
  debugGraphics.rect(bounds.x, bounds.y, bounds.width, bounds.height);

  // Optionally add a label (e.g., "Artwork Container")
  const labelStyle = new TextStyle({
    fontSize: 12,
    fill: 0xff0000,
    align: "center",
  });
  const label = new Text({
    text: "Artwork Container",
    style: labelStyle
  });
  label.x = bounds.x + bounds.width / 2;
  label.y = bounds.y - 20; // Adjust position above the rectangle
  label.anchor.set(0.5, 0);

  // Add the debug graphics and label to the debug layer
  layer.addChild(debugGraphics);
  layer.addChild(label);
};
